import { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { motion } from 'framer-motion'
import { ArrowUpRight, Globe, Menu, Share2, Check, X } from 'lucide-react'
import { useLanguage } from '../contexts/LanguageContext'
import { t } from '../locales/translations'
import { APP_ENTRY_URL, BRAND_MARK_URL } from '../config/brand'

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const [shareSuccess, setShareSuccess] = useState(false)
  const location = useLocation()
  const { language, toggleLanguage } = useLanguage()

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20)
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const navLinks = [
    { key: 'home', path: '/' },
    { key: 'features', path: '/features' },
    { key: 'download', path: '/download' },
    { key: 'docs', path: '/docs' },
    // { key: 'openCenter', path: '/open-center' },
  ]

  const isActivePath = (path) => {
    if (path === '/') {
      return location.pathname === '/'
    }

    return location.pathname === path || location.pathname.startsWith(`${path}/`)
  }

  const handleShare = async () => {
    const shareData = {
      title: 'Avelia',
      text: language === 'zh'
        ? 'Avelia，让智能在项目、设备与真实环境之间自然流动。'
        : 'Avelia brings intelligence into projects, devices, and real environments.',
      url: window.location.href
    }

    try {
      if (navigator.share) {
        await navigator.share(shareData)
      } else {
        await navigator.clipboard.writeText(window.location.href)
        setShareSuccess(true)
        setTimeout(() => setShareSuccess(false), 2000)
      }
    } catch (error) {
      // User cancelled share dialog.
    }
  }

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className={`fixed top-0 z-50 w-full transition-all duration-300 ${
        scrolled
          ? 'border-b border-border bg-white/96 shadow-[0_14px_32px_rgba(27,35,51,0.08)] backdrop-blur-md'
          : 'border-b border-transparent bg-white/90 backdrop-blur-sm'
      }`}
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-20 items-center justify-between">
          <Link to="/" className="group flex items-center space-x-3">
            <img
              src={BRAND_MARK_URL}
              alt="Avelia"
              className="h-12 w-12 rounded-2xl border border-primary/10 object-cover shadow-[0_10px_24px_rgba(255,95,135,0.16)] transition-transform duration-300 group-hover:scale-105"
            />
            <div>
              <h1 className="text-2xl font-bold text-text-primary transition-colors duration-300 group-hover:text-primary">
                AVELIA
              </h1>
              <p className="text-xs text-text-secondary/80">
                {language === 'zh' ? '持续工作的智能平台' : 'Intelligence for continuous work'}
              </p>
            </div>
          </Link>

          <div className="hidden items-center space-x-8 md:flex">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`relative text-sm font-medium uppercase tracking-[0.18em] transition-colors ${
                  isActivePath(link.path)
                    ? 'text-primary'
                    : 'text-text-secondary hover:text-primary'
                }`}
              >
                {t(language, `nav.${link.key}`)}
                {isActivePath(link.path) && (
                  <motion.div
                    layoutId="navbar-indicator"
                    className="absolute -bottom-1 left-0 right-0 h-0.5 rounded-full bg-primary"
                  />
                )}
              </Link>
            ))}

            <button
              onClick={toggleLanguage}
              className="flex items-center space-x-1.5 rounded-lg border border-border bg-white px-3 py-2 text-text-secondary transition-all hover:border-primary/30 hover:text-primary"
              title={language === 'zh' ? 'Switch to English' : 'Chinese'}
            >
              <Globe size={16} />
              <span className="text-xs font-mono">{language === 'zh' ? 'EN' : 'ZH'}</span>
            </button>

            <a
              href={APP_ENTRY_URL}
              className="flex items-center space-x-2 rounded-full bg-gradient-to-r from-primary to-secondary px-6 py-2.5 font-medium text-white transition-all hover:shadow-[0_16px_28px_rgba(255,95,135,0.24)]"
            >
              <span>{t(language, 'nav.downloadBtn')}</span>
              <ArrowUpRight size={18} />
            </a>

            <button
              onClick={handleShare}
              className="flex items-center space-x-2 rounded-lg border border-border bg-white px-4 py-2.5 text-text-secondary transition-all hover:border-primary/30 hover:text-primary"
              title={shareSuccess
                ? (language === 'zh' ? '链接已复制' : 'Link copied')
                : (language === 'zh' ? '分享页面' : 'Share page')}
            >
              {shareSuccess ? <Check size={18} className="text-primary" /> : <Share2 size={18} />}
              <span className="hidden text-sm font-medium lg:inline">
                {shareSuccess
                  ? (language === 'zh' ? '已复制' : 'Copied')
                  : (language === 'zh' ? '分享' : 'Share')}
              </span>
            </button>
          </div>

          <button
            onClick={() => setIsOpen(!isOpen)}
            className="rounded-lg border border-primary/20 p-2 glass-effect md:hidden"
          >
            {isOpen ? <X size={24} className="text-primary" /> : <Menu size={24} className="text-primary" />}
          </button>
        </div>
      </div>

      {isOpen && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-effect border-t border-primary/10 md:hidden"
        >
          <div className="space-y-4 px-4 py-6">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setIsOpen(false)}
                className={`block rounded-lg border px-4 py-2 transition-colors ${
                  isActivePath(link.path)
                    ? 'border-primary/30 bg-primary/10 text-primary'
                    : 'border-transparent text-text-secondary hover:bg-primary/5'
                }`}
              >
                {t(language, `nav.${link.key}`)}
              </Link>
            ))}

            <a
              href={APP_ENTRY_URL}
              className="flex w-full items-center justify-center space-x-2 rounded-full bg-gradient-to-r from-primary to-secondary px-4 py-3 font-medium text-white"
            >
              <span>{t(language, 'nav.downloadBtn')}</span>
              <ArrowUpRight size={18} />
            </a>

            <button
              onClick={() => {
                toggleLanguage()
                setIsOpen(false)
              }}
              className="flex w-full items-center justify-center space-x-2 rounded-lg border border-primary/15 px-4 py-2 text-text-secondary transition-all hover:text-primary"
            >
              <Globe size={18} />
              <span>{language === 'zh' ? 'Switch to English' : 'Chinese'}</span>
            </button>

            <button
              onClick={() => {
                handleShare()
                setIsOpen(false)
              }}
              className="flex w-full items-center justify-center space-x-2 rounded-lg border border-primary/15 px-4 py-2 text-text-secondary transition-all hover:text-primary"
            >
              {shareSuccess ? <Check size={18} className="text-primary" /> : <Share2 size={18} />}
              <span>{shareSuccess ? (language === 'zh' ? '链接已复制' : 'Link copied') : (language === 'zh' ? '分享页面' : 'Share page')}</span>
            </button>
          </div>
        </motion.div>
      )}
    </motion.nav>
  )
}

export default Navbar
